"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { useAuth } from "@/contexts/auth-context";
import { useAccountModal } from "@/contexts/account-modal-context";
import { ACCOUNT_PATH } from "@/lib/account/account-routes";
import { personInitial } from "@/lib/library/entry-copy";
import { LIBRARY_MINE_PATH } from "@/lib/library/library-routes";
import { Popover } from "@/components/ui/atoms/popover";
import { LegalLinks } from "@/components/legal/legal-links";

/**
 * Le point d'entrée permanent du compte, dans la zone 3 du header.
 *
 * Il ne convertit presque personne — un bouton de header n'a aucun contexte à
 * offrir — mais sans lui un inscrit ne peut pas revenir. C'est de
 * l'infrastructure, pas de l'acquisition, et c'est pour ça qu'il reste discret.
 *
 * Un seul bouton, pas le couple « Se connecter / S'inscrire » : deux boutons
 * dans un header sont du bruit, et la modale offre l'autre chemin en un clic.
 */
export function AccountControl() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { openAccountModal } = useAccountModal();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Pendant la résolution du jeton, on n'affiche rien plutôt qu'un « Se
  // connecter » qui disparaîtrait sous le curseur une demi-seconde plus tard.
  if (isLoading) {
    return null;
  }

  if (!isAuthenticated || user === null) {
    return (
      <button
        type="button"
        onClick={() => openAccountModal("header")}
        /* `py-[15px]` aligne le bouton sur les onglets du header. Quand la
           barre se comprime en paysage téléphone, ces 50 px de haut
           déborderaient sur la toolbar : une zone cliquable invisible
           par-dessus les contrôles du studio. */
        className="text-[13px] tracking-tight px-2 py-[15px] [@media(max-height:480px)]:py-1.5 cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
        style={{ color: "var(--color-text-muted)" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = "var(--color-text-primary)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = "var(--color-text-muted)";
        }}
      >
        Se connecter
      </button>
    );
  }

  /*
    Chez soi, l'adresse prend le relais d'un nom absent : c'est son propre
    écran, elle n'y apprend rien à personne. C'est chez les autres qu'on ne
    montre rien plutôt qu'une étiquette dérivée.
  */
  const ownLabel = user.nickname?.trim() || user.email;
  const initial = personInitial(ownLabel);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label="Mon compte"
        aria-expanded={menuOpen}
        className="w-6 h-6 grid place-items-center rounded-full text-[11px] font-semibold cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        style={{
          backgroundColor: "var(--color-accent-subtle)",
          color: "var(--color-text-primary)",
        }}
      >
        {initial}
      </button>

      <Popover
        anchorRef={anchorRef}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        placement="bottom-end"
        offset={8}
      >
        <div className="min-w-[200px] py-1">
          <div className="px-3 py-2">
            <p className="text-[13px] font-medium text-text-primary truncate">{ownLabel}</p>
            {user.nickname?.trim() ? (
              <p className="mt-0.5 text-[11px] text-text-muted truncate">{user.email}</p>
            ) : null}
          </div>

          <div
            aria-hidden
            className="h-px my-1"
            style={{ backgroundColor: "var(--color-glass-separator)" }}
          />

          {/* Le nom, le forfait, les documents acceptés et la suppression du
              compte vivent sur « Mon compte » : le menu n'en garde que le
              chemin, et les deux gestes qu'on fait sans vouloir s'arrêter. */}
          <Link
            href={ACCOUNT_PATH}
            onClick={() => setMenuOpen(false)}
            className="block w-full text-left px-3 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors cursor-pointer"
          >
            Mon compte
          </Link>

          {/* Un lien depuis que « Mes morceaux » a une adresse : le menu du
              compte n'a plus à connaître l'état d'ouverture d'une modale, il
              pointe une route comme n'importe quelle autre entrée. */}
          <Link
            href={LIBRARY_MINE_PATH}
            onClick={() => setMenuOpen(false)}
            className="block w-full text-left px-3 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors cursor-pointer"
          >
            Mes morceaux
          </Link>

          {/* Pas de « Réglages » : ils vivent dans la Toolbar, les dupliquer
              ici créerait deux chemins pour la même chose. */}

          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              logout();
            }}
            className="w-full text-left px-3 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-colors cursor-pointer"
          >
            Se déconnecter
          </button>

          {/* Les documents juridiques restent atteignables d'ici aussi : la
              coque iOS exige la confidentialité depuis n'importe quel écran.

              Libellés courts, parce que les trois libellés pleins font 300 px
              et que ce menu en fait 200 : ils passeraient à la ligne sur un
              séparateur. */}
          <div
            aria-hidden
            className="h-px my-1"
            style={{ backgroundColor: "var(--color-glass-separator)" }}
          />

          <LegalLinks
            labels="short"
            onNavigate={() => setMenuOpen(false)}
            className="px-3 py-1.5 text-[11px] leading-[16px] text-text-muted"
          />
        </div>
      </Popover>
    </>
  );
}
